/**
 * ChatAdmin — /admin/chat (#1181, v2.516.1).
 *
 * Admin-Dashboard für Chatbot: Top-Users, Token-Trend, Tool-Häufigkeit,
 * Cost-Estimate, durchsuchbares Audit-Log.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader, Card, Badge } from '../../components/ui';
import { Activity, Coins, Database, Search, RefreshCw } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';

const API = '';

function centsToEur(c) {
  if (c == null) return '–';
  return `€${(c / 100).toFixed(2)}`;
}

function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' });
}

export default function ChatAdmin() {
  const [days, setDays] = useState(30);
  const [usage, setUsage] = useState([]);
  const [trend, setTrend] = useState([]);
  const [tools, setTools] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, tr, tl, a] = await Promise.all([
        fetch(`${API}/api/admin/chat/usage?days=${days}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/admin/chat/trend?days=${days}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/admin/chat/tools?days=${days}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/admin/chat/audit?limit=100`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setUsage(u?.rows || []);
      setTrend(tr?.rows || []);
      setTools(tl?.rows || []);
      setAudit(Array.isArray(a) ? a : []);
    } catch (e) {
      console.error('load failed', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const searchAudit = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/admin/chat/audit?limit=200${auditSearch ? `&search=${encodeURIComponent(auditSearch)}` : ''}`,
        { credentials: 'include' }
      );
      const d = await r.json();
      setAudit(Array.isArray(d) ? d : []);
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    return usage.reduce((acc, r) => ({
      users: acc.users + 1,
      messages: acc.messages + Number(r.messages || 0),
      inputTokens: acc.inputTokens + Number(r.input_tokens || 0),
      outputTokens: acc.outputTokens + Number(r.output_tokens || 0),
      costCents: acc.costCents + Number(r.cost_cents || 0),
    }), { users: 0, messages: 0, inputTokens: 0, outputTokens: 0, costCents: 0 });
  }, [usage]);

  const trendData = useMemo(() => trend.map(r => ({
    date: r.date ? String(r.date).slice(5, 10) : '',
    tokens: Number(r.input_tokens || 0) + Number(r.output_tokens || 0),
    cost: Number(r.cost_cents || 0) / 100,
    messages: Number(r.messages || 0),
  })), [trend]);

  return (
    <div className="p-6">
      <PageHeader
        title="Chatbot Admin"
        subtitle="Token-Verbrauch · Tool-Statistiken · Audit-Log"
      />

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-[var(--text-secondary)]">Zeitraum:</span>
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-[8px] text-sm border transition-colors ${
              days === d
                ? 'bg-[#338edc] text-white border-[#338edc]'
                : 'bg-surface-card text-[var(--text-primary)] border-line hover:bg-surface-hover'
            }`}
          >
            {d} Tage
          </button>
        ))}
        <button
          onClick={load}
          disabled={loading}
          className="ml-2 p-1.5 rounded-[8px] border border-line bg-surface-card hover:bg-surface-hover text-[var(--text-secondary)] disabled:opacity-50"
          title="Reload"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi icon={<Activity size={16} />} label="Active Users" value={totals.users} />
        <Kpi icon={<Database size={16} />} label="Messages" value={totals.messages.toLocaleString('de-AT')} />
        <Kpi icon={<Database size={16} />} label="Input Tokens" value={(totals.inputTokens).toLocaleString('de-AT')} />
        <Kpi icon={<Database size={16} />} label="Output Tokens" value={(totals.outputTokens).toLocaleString('de-AT')} />
        <Kpi icon={<Coins size={16} />} label="Geschätzte Kosten" value={centsToEur(totals.costCents)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="px-4 pt-3 pb-2 text-sm font-semibold text-[var(--text-primary)]">Token-Trend</div>
          <div className="h-64 px-2 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip />
                <Line type="monotone" dataKey="tokens" stroke="#338edc" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <div className="px-4 pt-3 pb-2 text-sm font-semibold text-[var(--text-primary)]">Tool-Aufrufe</div>
          <div className="h-64 px-2 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tools.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="tool_name" tick={{ fontSize: 10, fill: '#6B7280' }} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip />
                <Bar dataKey="calls" fill="#338edc" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="px-4 pt-3 pb-2 text-sm font-semibold text-[var(--text-primary)]">Top Users nach Kosten</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-input text-xs uppercase text-[var(--text-tertiary)]">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-right">Messages</th>
                  <th className="px-3 py-2 text-right">Tokens (in/out)</th>
                  <th className="px-3 py-2 text-right">Kosten</th>
                </tr>
              </thead>
              <tbody>
                {usage.slice(0, 15).map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="px-3 py-2 text-[var(--text-primary)]">
                      {r.vorname} {r.nachname}
                      <div className="text-xs text-[var(--text-tertiary)]">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{Number(r.messages || 0).toLocaleString('de-AT')}</td>
                    <td className="px-3 py-2 text-right text-[var(--text-secondary)]">
                      {Number(r.input_tokens || 0).toLocaleString('de-AT')} / {Number(r.output_tokens || 0).toLocaleString('de-AT')}
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--text-primary)] font-medium">{centsToEur(r.cost_cents)}</td>
                  </tr>
                ))}
                {usage.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-[var(--text-tertiary)]">Keine Daten</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
        <Card>
          <div className="px-4 pt-3 pb-2 text-sm font-semibold text-[var(--text-primary)]">Tool-Statistiken</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-input text-xs uppercase text-[var(--text-tertiary)]">
                <tr>
                  <th className="px-3 py-2 text-left">Tool</th>
                  <th className="px-3 py-2 text-right">Calls</th>
                  <th className="px-3 py-2 text-right">Errors</th>
                  <th className="px-3 py-2 text-right">Ø ms</th>
                  <th className="px-3 py-2 text-right">Max ms</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="px-3 py-2 font-mono text-xs text-[var(--text-primary)]">{r.tool_name}</td>
                    <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{Number(r.calls).toLocaleString('de-AT')}</td>
                    <td className="px-3 py-2 text-right">
                      {Number(r.errors) > 0 ? <Badge tone="red">{r.errors}</Badge> : <span className="text-[var(--text-tertiary)]">0</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{r.avg_ms ?? '–'}</td>
                    <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{r.max_ms ?? '–'}</td>
                  </tr>
                ))}
                {tools.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--text-tertiary)]">Keine Daten</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card>
        <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-line">
          <div className="text-sm font-semibold text-[var(--text-primary)]">Audit-Log (letzte 100)</div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={auditSearch}
              onChange={(e) => setAuditSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchAudit(); }}
              placeholder="Suche in Query/Input…"
              className="pl-8 pr-2 py-1.5 text-sm rounded-[8px] border border-line bg-surface-input text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] w-64"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-input text-[10px] uppercase text-[var(--text-tertiary)]">
              <tr>
                <th className="px-3 py-2 text-left">Zeit</th>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Event</th>
                <th className="px-3 py-2 text-left">Tool</th>
                <th className="px-3 py-2 text-left">Query / Input</th>
                <th className="px-3 py-2 text-right">Rows</th>
                <th className="px-3 py-2 text-right">ms</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((r) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 text-[var(--text-tertiary)] whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">
                    {r.vorname || r.email || `#${r.user_id}`}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={r.event_type === 'tool_error' || r.event_type === 'error' ? 'red' : 'blue'}>{r.event_type}</Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-[var(--text-secondary)]">{r.tool_name || '–'}</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] max-w-md truncate">
                    {r.query || (r.tool_input ? JSON.stringify(r.tool_input) : '')}
                  </td>
                  <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{r.rows_returned ?? '–'}</td>
                  <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{r.duration_ms ?? '–'}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--text-tertiary)]">Kein Log</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <Card padding="sm">
      <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)] mb-1">{icon}{label}</div>
      <div className="text-xl font-semibold text-[var(--text-primary)]">{value}</div>
    </Card>
  );
}
